import { Nullable, NullableResultPromise } from "../base-types";
import { IEntity } from "../contracts/base/entity";
import { IEntityLoadConfiguration } from "../contracts/querying/entity-load-configuration";
import { IEntityQueryResult } from "../contracts/querying/entity-query-result";
import { IEntitySearchAfterResult } from "../contracts/querying/entity-search-after-result";
import { IIdQueryResult } from "../contracts/querying/id-query-result";
import { Query } from "../contracts/querying/query";
import { IQueryLoadConfiguration, QueryLoadConfiguration } from "../contracts/querying/query-load-configuration";
import { SearchAfterQuery } from "../contracts/querying/search-after-query";
import ErrorMessages from "../error-messages";
import { InvalidOperationError } from "../errors/invalid-operation-error";
import { Guard } from "../guard";
import { LoadOptionsMapper } from "../mappers/load-options-mapper";
import { QueryingMapper } from "../mappers/querying-mapper";
import { IContentHubClient } from "./content-hub-client";
import { IExtendedContentHubClient } from "./extended-client";
import { CancelCallback } from "./internal-client";
import { SchemaQuerying } from "./schema-querying";

export interface IQueryingClient {
    singleAsync(
        query: Query,
        loadConfiguration?: IEntityLoadConfiguration,
        cancelCallback?: CancelCallback
    ): NullableResultPromise<IEntity>;

    singleIdAsync(query: Query, cancelCallback?: CancelCallback): NullableResultPromise<number>;

    queryAsync(
        query: Query,
        loadConfiguration?: Nullable<IEntityLoadConfiguration>,
        cancelCallback?: CancelCallback
    ): Promise<IEntityQueryResult>;

    queryIdsAsync(query: Query, cancelCallback?: CancelCallback): Promise<IIdQueryResult>;

    searchAfterAsync(query: SearchAfterQuery, cancelCallback?: CancelCallback): Promise<IEntitySearchAfterResult>;
}

/**
 *  Contains functionality to query and scroll for entities.
 */
abstract class QueryingClientBase implements IQueryingClient {
    protected readonly _client: IContentHubClient;

    constructor(client: IContentHubClient) {
        Guard.notNullOrUndefined(client);
        this._client = client;
    }

    /**
     * Gets a single entity that matches the query.
     *
     * @param query - The query to execute
     * @param loadConfiguration - The load configuration. If it is null, then {@link EntityLoadConfiguration#Default} will be used
     * @param cancelCallback - A {@link CancelCallback} that will be placed in an Axios {@link CancelToken} if provided
     * @returns The entity that matches the query or null if there are no results.
     */
    async singleAsync(
        query: Query,
        loadConfiguration?: IEntityLoadConfiguration,
        cancelCallback?: CancelCallback
    ): NullableResultPromise<IEntity> {
        Guard.notNullOrUndefined(query);

        const result = await this.queryAsync(query, loadConfiguration, cancelCallback);

        if (result.items.length === 0) {
            return null;
        } else if (result.items.length > 1) {
            throw new InvalidOperationError(ErrorMessages.QueryingClient.MultipleResults);
        }

        return result.items[0];
    }

    /**
     * Gets a the id of the entity that matches the query.
     *
     * @param query - The query to execute
     * @param cancelCallback - A {@link CancelCallback} that will be placed in an Axios {@link CancelToken} if provided
     * @returns The id of the entity that matches the query or null if there are no results.
     */
    async singleIdAsync(query: Query, cancelCallback?: CancelCallback): NullableResultPromise<number> {
        Guard.notNullOrUndefined(query);

        const result = await this.queryIdsAsync(query, cancelCallback);

        if (result.items.length === 0) {
            return null;
        } else if (result.items.length > 1) {
            throw new InvalidOperationError(ErrorMessages.QueryingClient.MultipleResults);
        }

        return result.items[0];
    }

    /**
     * Retrieves entities matching the query.
     *
     * @param query - The query to execute
     * @param loadConfiguration - The load configuration. If it is null, then {@link EntityLoadConfiguration#Default} will be used
     * @param cancelCallback - A {@link CancelCallback} that will be placed in an Axios {@link CancelToken} if provided
     */
    abstract queryAsync(
        query: Query,
        loadConfiguration?: Nullable<IEntityLoadConfiguration>,
        cancelCallback?: CancelCallback
    ): Promise<IEntityQueryResult>;

    /**
     * Retrieves the ids of entities matching the query.
     *
     * @param query - The query to execute
     * @param cancelCallback - A {@link CancelCallback} that will be placed in an Axios {@link CancelToken} if provided
     */
    abstract queryIdsAsync(query: Query, cancelCallback?: CancelCallback): Promise<IIdQueryResult>;

    /**
     * Retrieves entities matching the query.
     *
     * @param query - The query to execute
     * @param cancelCallback - A {@link CancelCallback} that will be placed in an Axios {@link CancelToken} if provided
     */
    abstract searchAfterAsync(
        query: SearchAfterQuery,
        cancelCallback?: CancelCallback
    ): Promise<IEntitySearchAfterResult>;

    //? deprecated
    protected resolveLoadConfiguration(
        query: Query,
        loadConfiguration?: IEntityLoadConfiguration
    ): Nullable<IQueryLoadConfiguration> {
        let queryLoadConfiguration: Nullable<IQueryLoadConfiguration>;

        if (loadConfiguration != null) {
            queryLoadConfiguration = LoadOptionsMapper.toQueryLoadConfiguration(loadConfiguration);
        } else {
            queryLoadConfiguration = QueryLoadConfiguration.Default;
        }

        return queryLoadConfiguration;
    }
}

/**
 *  Contains functionality to query and scroll for entities.
 */
export class QueryingClient extends QueryingClientBase {
    private readonly _extendedClient: IExtendedContentHubClient;
    private readonly _mapper: QueryingMapper;
    private readonly _schemaQuerying: SchemaQuerying;

    constructor(client: IExtendedContentHubClient) {
        super(client);

        Guard.notNullOrUndefined(client);
        this._extendedClient = client;
        this._schemaQuerying = new SchemaQuerying(client);
        this._mapper = new QueryingMapper(client);
    }

    /**
     * Retrieves entities matching the query.
     *
     * @param query - The query to execute
     * @param loadConfiguration - The load configuration. If it is null, then {@link EntityLoadConfiguration#Default} will be used
     * @param cancelCallback - A {@link CancelCallback} that will be placed in an Axios {@link CancelToken} if provided
     */
    async queryAsync(
        query: Query,
        loadConfiguration?: Nullable<IEntityLoadConfiguration>,
        cancelCallback?: CancelCallback
    ): Promise<IEntityQueryResult> {
        Guard.notNullOrUndefined(query, "query");
        //TODO query.validate();

        const queryLoadConfiguration = this.resolveLoadConfiguration(query, loadConfiguration || undefined);
        const resource = await this._schemaQuerying.queryWithSchemaAsync(
            query,
            queryLoadConfiguration!,
            cancelCallback
        );

        loadConfiguration = LoadOptionsMapper.toEntityLoadConfiguration(queryLoadConfiguration || undefined);
        const result = await this._mapper.mapEntityQueryResultAsync(resource, query, loadConfiguration!);

        return result!;
    }

    /**
     * Retrieves the ids of entities matching the query.
     *
     * @param query - The query to execute
     * @param cancelCallback - A {@link CancelCallback} that will be placed in an Axios {@link CancelToken} if provided
     */
    async queryIdsAsync(query: Query, cancelCallback?: CancelCallback): Promise<IIdQueryResult> {
        Guard.notNullOrUndefined(query, "query");
        //TODO query.validate();

        const resource = await this._schemaQuerying.queryWithSchemaAsync(
            query,
            QueryLoadConfiguration.Ids,
            cancelCallback
        );

        const result = await this._mapper.mapEntityIdQueryResultAsync(resource, query);

        return result!;
    }

    /**
     * Retrieves entities matching the query.
     *
     * @param query - The query to execute
     * @param cancelCallback - A {@link CancelCallback} that will be placed in an Axios {@link CancelToken} if provided
     */
    async searchAfterAsync(
        query: SearchAfterQuery,
        cancelCallback?: CancelCallback
    ): Promise<IEntitySearchAfterResult> {
        Guard.notNullOrUndefined(query, "query");
        Guard.notNullOrUndefined(query.filter, "query.filter");

        const resource = await this._schemaQuerying.searchAfterWithSchemaAsync(query, cancelCallback);

        const result = await this._mapper.mapEntitySearchAfterResult(resource);

        return result!;
    }
}
